import { useState } from 'react';
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom';
import {
  CalendarDays,
  ExternalLink,
  Image,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareQuote,
  Package,
  Settings,
  Star,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import Logo from '../../components/Logo';
import { useAuth } from '../../context/AuthContext';
import { firebaseReady } from '../../lib/firebase';

const nav = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, adminOnly: true },
  { to: '/admin/bookings', label: 'Bookings', icon: CalendarDays, adminOnly: false },
  { to: '/admin/packages', label: 'Packages & Add-ons', icon: Package, adminOnly: true },
  { to: '/admin/services', label: 'Custom Services', icon: Wrench, adminOnly: true },
  { to: '/admin/quotes', label: 'Quotes & Messages', icon: MessageSquareQuote, adminOnly: true },
  { to: '/admin/gallery', label: 'Gallery', icon: Image, adminOnly: true },
  { to: '/admin/testimonials', label: 'Reviews', icon: Star, adminOnly: true },
  { to: '/admin/team', label: 'Team', icon: Users, adminOnly: true },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings, adminOnly: true },
];

export default function AdminLayout() {
  const { user, isAdmin, isStaff, role, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  if (!firebaseReady) return <Navigate to="/admin/login" replace />;
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-night-950 text-sm text-zinc-500">Loading…</div>;
  }
  if (!user || !isStaff) return <Navigate to="/admin/login" replace />;

  const visibleNav = nav.filter((n) => isAdmin || !n.adminOnly);

  return (
    <div className="flex min-h-screen bg-night-950">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-white/[0.06] bg-night-900 transition-transform lg:static lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-[72px] items-center justify-between border-b border-white/[0.06] px-5">
          <Link to="/admin"><Logo size={30} /></Link>
          <button className="text-zinc-400 lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gradient-to-r from-accent-600/25 to-transparent text-accent-300'
                    : 'text-zinc-400 hover:bg-white/[0.04] hover:text-white'
                }`
              }
            >
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-white/[0.06] p-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-zinc-400 transition-colors hover:bg-white/[0.04] hover:text-white">
            <ExternalLink size={17} /> View Website
          </a>
          <button onClick={() => signOut()} className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-zinc-400 transition-colors hover:bg-white/[0.04] hover:text-red-300">
            <LogOut size={17} /> Sign Out
          </button>
          <p className="truncate px-3.5 pt-1 text-[11px] text-zinc-600">
            {user.email} · {role === 'admin' ? 'admin' : 'cleaner'}
          </p>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[60px] items-center gap-3 border-b border-white/[0.06] bg-night-900/60 px-4 lg:hidden">
          <button className="text-zinc-300" onClick={() => setOpen(true)} aria-label="Open menu"><Menu size={22} /></button>
          <span className="font-display font-bold text-white">Admin</span>
        </header>
        <main className="flex-1 p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
